import { Route } from '@angular/router';
import { authGuard, publicOnlyGuard } from './core/index';
import { settingsRoutes } from './features/settings/settings.routes';
import {
  structureRoutes,
  estructuraRoutes,
} from './features/structure/structure.routes';
import { complianceRoutes } from './features/compliance/compliance.routes';

export const appRoutes: Route[] = [
  // Ruta por defecto
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },

  // Login (sin layout, solo si NO está autenticado)
  {
    path: 'login',
    canActivate: [publicOnlyGuard],
    loadComponent: () =>
      import('./features/auth/login/login.component').then(
        (m) => m.LoginComponent,
      ),
  },

  // Rutas con layout (requiere autenticación)
  {
    path: 'app',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layout-wrapper').then((m) => m.LayoutWrapperComponent),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/dashboard/dashboard.component').then(
                (m) => m.DashboardComponent,
              ),
          },
          {
            path: 'treasury',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-treasury/dashboard-treasury.component'
              ).then((m) => m.DashboardTreasuryComponent),
          },
          {
            path: 'administration',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-administration/dashboard-administration.component'
              ).then((m) => m.DashboardAdministrationComponent),
          },
          {
            path: 'operation',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-operation/dashboard-operation.component'
              ).then((m) => m.DashboardOperationComponent),
          },
          {
            path: 'compliance',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-compliance/dashboard-compliance.component'
              ).then((m) => m.DashboardComplianceComponent),
          },
          {
            path: 'courier',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-courier/dashboard-courier.component'
              ).then((m) => m.DashboardCourierComponent),
          },
          {
            path: 'hr',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-hr/dashboard-hr.component'
              ).then((m) => m.DashboardHrComponent),
          },
          {
            path: 'audit',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-audit/dashboard-audit.component'
              ).then((m) => m.DashboardAuditComponent),
          },
          {
            path: 'support',
            loadComponent: () =>
              import(
                './features/dashboard/dashboard-support/dashboard-support.component'
              ).then((m) => m.DashboardSupportComponent),
          },
        ],
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(
            (m) => m.ProfileComponent,
          ),
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import('./features/change-password/change-password.component').then(
            (m) => m.ChangePasswordComponent,
          ),
      },

      {
        path: 'chat/:id',
        data: { kind: 'staff' },
        loadComponent: () =>
          import('@shared-data-access/services/inbox/chat-thread.component').then(
            (m) => m.ChatThreadComponent,
          ),
      },

      // ── Estructura ─────────────────────────────────────────────────────────
      {
        path: 'structure',
        children: structureRoutes,
      },
      // Alias en español
      {
        path: 'estructura',
        children: estructuraRoutes,
      },

      // ── Clientes ───────────────────────────────────────────────────────────
      {
        path: 'clients',
        loadComponent: () =>
          import('./features/clients/clients.component').then(
            (m) => m.ClientsComponent,
          ),
      },
      {
        path: 'clients/new',
        loadComponent: () =>
          import('./features/clients/clients.component').then(
            (m) => m.ClientsComponent,
          ),
      },
      {
        path: 'clients/:id/edit',
        loadComponent: () =>
          import('./features/clients/clients.component').then(
            (m) => m.ClientsComponent,
          ),
      },
      {
        path: 'clients/:id/view',
        loadComponent: () =>
          import('./features/clients/clients.component').then(
            (m) => m.ClientsComponent,
          ),
      },

      // ── Operaciones ────────────────────────────────────────────────────────
      {
        path: 'operations',
        children: [
          {
            path: 'transfers',
            loadComponent: () =>
              import('./features/operations/transfers/transfers.component').then(
                (m) => m.TransfersComponent,
              ),
          },
          {
            path: 'packages',
            loadComponent: () =>
              import('./features/operations/packages/packages.component').then(
                (m) => m.PackagesComponent,
              ),
          },
          {
            path: 'currency-exchange',
            loadComponent: () =>
              import(
                './features/operations/currency-exchange/currency-exchange.component'
              ).then((m) => m.CurrencyExchangeComponent),
          },
          {
            path: 'additional-service',
            loadComponent: () =>
              import(
                './features/operations/additional-service/additional-service.component'
              ).then((m) => m.AdditionalServiceComponent),
          },
        ],
      },

      // ── Cumplimiento ───────────────────────────────────────────────────────
      {
        path: 'compliance',
        children: complianceRoutes,
      },

      // ── Logística ──────────────────────────────────────────────────────────
      {
        path: 'logistics',
        children: [
          {
            path: 'catalogs',
            loadComponent: () =>
              import('./features/logistics/catalogs/catalogs.component').then(
                (m) => m.CatalogsComponent,
              ),
          },
          {
            path: 'warehouses',
            loadComponent: () =>
              import('./features/logistics/warehouses/warehouses.component').then(
                (m) => m.WarehousesComponent,
              ),
          },
          {
            path: 'vehicles',
            loadComponent: () =>
              import('./features/logistics/vehicles/vehicles.component').then(
                (m) => m.VehiclesComponent,
              ),
          },
          {
            path: 'routes',
            loadComponent: () =>
              import('./features/logistics/routes/routes.component').then(
                (m) => m.RoutesComponent,
              ),
          },
        ],
      },

      // ── Soporte ────────────────────────────────────────────────────────────
      {
        path: 'support',
        children: [
          {
            path: 'internal',
            loadComponent: () =>
              import('./features/support/internal/internal.component').then(
                (m) => m.InternalComponent,
              ),
          },
          {
            path: 'clients',
            loadComponent: () =>
              import('./features/support/clients/clients.component').then(
                (m) => m.SupportClientsComponent,
              ),
          },
        ],
      },

      // ── Reportes ───────────────────────────────────────────────────────────
      {
        path: 'reports',
        children: [
          {
            path: 'movement-log',
            loadComponent: () =>
              import('./features/reports/movement-log/movement-log.component').then(
                (m) => m.MovementLogComponent,
              ),
          },
          {
            path: 'fund-treasury',
            loadComponent: () =>
              import('./features/reports/fund-treasury/fund-treasury.component').then(
                (m) => m.FundTreasuryComponent,
              ),
          },
          {
            path: 'remittances-balances',
            loadComponent: () =>
              import('./features/reports/remittances-balances/remittances-balances.component').then(
                (m) => m.RemittancesBalancesComponent,
              ),
          },
          {
            path: 'pending',
            loadComponent: () =>
              import('./features/reports/pending/pending.component').then(
                (m) => m.PendingComponent,
              ),
          },
          {
            path: 'pending-ext-fund',
            loadComponent: () =>
              import('./features/reports/pending-ext-fund/pending-ext-fund.component').then(
                (m) => m.PendingExtFundComponent,
              ),
          },
          {
            path: 'pending-higher-balance',
            loadComponent: () =>
              import('./features/reports/pending-higher-balance/pending-higher-balance.component').then(
                (m) => m.PendingHigherBalanceComponent,
              ),
          },
          {
            path: 'dealer',
            loadComponent: () =>
              import('./features/reports/dealer/dealer.component').then(
                (m) => m.DealerComponent,
              ),
          },
          {
            path: 'operative',
            loadComponent: () =>
              import('./features/reports/operative/operative.component').then(
                (m) => m.OperativeComponent,
              ),
          },
          {
            path: 'received-packages',
            loadComponent: () =>
              import('./features/reports/received-packages/received-packages.component').then(
                (m) => m.ReceivedPackagesComponent,
              ),
          },
          {
            path: 'accumulated',
            loadComponent: () =>
              import('./features/reports/accumulated/accumulated.component').then(
                (m) => m.AccumulatedComponent,
              ),
          },
        ],
      },

      // ── Tesorería ──────────────────────────────────────────────────────────
      {
        path: 'treasure',
        children: [
          {
            path: 'bank-accounts',
            loadComponent: () =>
              import('./features/treasure/bank-accounts/bank-accounts.component').then(
                (m) => m.BankAccountsComponent,
              ),
          },
        ],
      },

      // ── Configuraciones ────────────────────────────────────────────────────
      {
        path: 'settings',
        children: settingsRoutes,
      },
    ],
  },

  // 404 → login
  {
    path: '**',
    redirectTo: 'login',
  },
];
